import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@material-ui/core';
import { compact, truncate } from 'lodash';
import { CircleArrowLeft, CircleArrowRight } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../../layout/Flex';
import { toFullName } from '../../../../../../../services/user';
import { useProductAnalytics } from '../../../../../../features/Experience/services/productAnalytics';
import { WithBreakpoints } from '../../../components/Breakpoints';
import { EmployeeAppCard } from '../../../components/EmployeeAppCard';
import {
  MessageArchiveButton,
  MessageBody
} from '../../../components/Messages';
import { PlainDate } from '../../../components/Tasks';
import { getIndividualInstanceUrl } from '../../../services/routes';

const Container = styled('div')((p) => ({
  width: '100%'
}));

export const InboxMessageDetails = ({
  instance,
  message,
  actorAccessToken,
  instanceAccessToken,
  returnTo,
  nextButtons = EMPTY_ARR,
  noBackButton = false,
  showTemplateTitle = false,
  additionalLinkInHeader = null
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  message: IExperienceMessageDenormalized;
  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
  noBackButton?: boolean;
  showTemplateTitle?: boolean;
  nextButtons?: {
    label: string;
    url: string;
  }[];
  additionalLinkInHeader?: {
    label: string;
    url: string;
  } | null;
}) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const { t } = useUnsafeTranslation();

  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between tasks, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [message.templateId]);

  const backUrl =
    returnTo || getIndividualInstanceUrl(actorAccessToken, instanceAccessToken);

  const isSupportingActor = message.actor !== PARTICIPANT_ACTOR_KEY;
  const showSubTitle = showTemplateTitle || isSupportingActor;

  const title = (
    <FlexItem flex={3}>
      <FlexContainerVertical spacing={0.5}>
        <Typography variant="h4">{message.subject}</Typography>
        {showSubTitle && (
          <FlexContainer spacing={3} wrap="wrap" rowSpacing={0.5}>
            <Typography variant="h5" color="textSecondary">
              {compact([
                showTemplateTitle && instance.data.title,
                isSupportingActor && toFullName(instance.data.participant)
              ]).join(' • ')}
            </Typography>
            {additionalLinkInHeader && (
              <Link to={additionalLinkInHeader.url}>
                <Typography variant="body2" color="primary">
                  {additionalLinkInHeader.label} <CircleArrowRight size={12} />
                </Typography>
              </Link>
            )}
          </FlexContainer>
        )}
      </FlexContainerVertical>
    </FlexItem>
  );

  return (
    <Container ref={ref}>
      <FlexContainerVertical fullWidth spacing={3}>
        <FlexContainerVertical fullWidth spacing={2}>
          {!noBackButton && (
            <Link
              to={backUrl}
              onClick={
                () => undefined
                // TODO
                // trackEvent({
                //   eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
                //   name: 'Back Link Clicked',
                //   Explanation:
                //     'A user clicked the back link at the top of the single task page',
                //   'Task ID': task.id,
                //   'Back URL': backUrl
                // })
              }
            >
              <IconButton color="primary">
                <CircleArrowLeft size={24} />
              </IconButton>
            </Link>
          )}
          <WithBreakpoints
            lg={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <MessageArchiveButton
                      d={message}
                      instanceAccessToken={instanceAccessToken}
                    />
                    <PlainDate ts={message.sendAt} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            md={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <MessageArchiveButton
                      d={message}
                      instanceAccessToken={instanceAccessToken}
                    />
                    <PlainDate ts={message.sendAt} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            sm={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <MessageArchiveButton
                      d={message}
                      instanceAccessToken={instanceAccessToken}
                    />
                    <PlainDate ts={message.sendAt} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            xs={
              <FlexContainerVertical fullWidth spacing={1}>
                {title}
                <FlexContainer>
                  <MessageArchiveButton
                    d={message}
                    instanceAccessToken={instanceAccessToken}
                  />
                  <PlainDate ts={message.sendAt} />
                </FlexContainer>
              </FlexContainerVertical>
            }
          />
          <EmployeeAppCard>
            <MessageBody d={message} />
          </EmployeeAppCard>
        </FlexContainerVertical>
        {nextButtons.length > 0 && (
          <FlexContainer>
            {nextButtons.map((nextButton, i) => (
              <Link to={nextButton.url} key={i}>
                <Button color="primary" variant={'outlined'}>
                  {truncate(nextButton.label, {
                    length: Math.round(100 / nextButtons.length)
                  })}
                </Button>
              </Link>
            ))}
          </FlexContainer>
        )}
      </FlexContainerVertical>
    </Container>
  );
};
